import apiClient, { getErrorMessage } from './api.service';
import { API_ENDPOINTS } from '../config/api';

export interface AccessCode {
  _id: string;
  userId: {
    _id: string;
    name: string;
    email: string;
  };
  code: string;
  paymentType: 'full' | 'partial';
  amount?: number;
  status: 'active' | 'used' | 'expired' | 'invalidated';
  expiresAt: string;
  redeemedAt?: string;
  generatedBy: {
    _id: string;
    name: string;
    email: string;
  };
  bookingId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface GenerateAccessCodeRequest {
  email: string;
  paymentType: 'full' | 'partial';
  amount?: number;
}

export interface RedeemAccessCodeRequest {
  email: string;
  code: string;
}

export interface AccessCodeResponse {
  status: string;
  message: string;
  data: {
    accessCode: AccessCode;
  };
}

export interface RedeemAccessCodeResponse {
  status: string;
  message: string;
  data: {
    user: {
      _id: string;
      name: string;
      email: string;
      hasBookingAccess: boolean;
      bookingAccessGrantedAt: string;
    };
  };
}

export interface AccessCodeListResponse {
  status: string;
  data: {
    accessCodes: AccessCode[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
    };
  };
}

export interface AccessCodeStatsResponse {
  status: string;
  data: {
    stats: {
      total: number;
      active: number;
      used: number;
      expired: number;
      invalidated: number;
      byPaymentType: {
        full: number;
        partial: number;
      };
    };
  };
}

class AccessCodeService {
  /**
   * Redeem an access code (public - no auth required)
   */
  async redeemAccessCode(data: RedeemAccessCodeRequest): Promise<RedeemAccessCodeResponse> {
    try {
      const response = await apiClient.post<RedeemAccessCodeResponse>(
        API_ENDPOINTS.ACCESS_CODES.REDEEM,
        data
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Generate an access code for a user (admin only)
   */
  async generateAccessCode(data: GenerateAccessCodeRequest): Promise<AccessCodeResponse> {
    try {
      const response = await apiClient.post<AccessCodeResponse>(
        API_ENDPOINTS.ACCESS_CODES.GENERATE,
        data
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Get all access codes with optional filters (admin only)
   */
  async getAllAccessCodes(params?: {
    page?: number;
    limit?: number;
    status?: string;
    paymentType?: string;
  }): Promise<AccessCodeListResponse> {
    try {
      const response = await apiClient.get<AccessCodeListResponse>(
        API_ENDPOINTS.ACCESS_CODES.GET_ALL,
        { params }
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Get access code statistics (admin only)
   */
  async getAccessCodeStats(): Promise<AccessCodeStatsResponse> {
    try {
      const response = await apiClient.get<AccessCodeStatsResponse>(
        API_ENDPOINTS.ACCESS_CODES.GET_STATS
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }
}

const accessCodeService = new AccessCodeService();
export default accessCodeService;
